"use client"

import { useState, useEffect, useCallback } from "react"
import { WifiOff, Wifi, RefreshCw, AlertTriangle } from "lucide-react"
import { getQueueSize } from "@/lib/offline-queue"

type SyncStatus = "idle" | "syncing" | "error"

export function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(true)
  const [showReconnected, setShowReconnected] = useState(false)
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle")
  const [queueCount, setQueueCount] = useState(0)

  // Refresh queue count from IndexedDB
  const refreshQueueCount = useCallback(async () => {
    try {
      const count = await getQueueSize()
      setQueueCount(count)
    } catch {
      // IndexedDB may not be available during SSR
    }
  }, [])

  useEffect(() => {
    // Defer initial sync setState to next microtask to avoid cascading renders
    queueMicrotask(() => {
      setIsOnline(navigator.onLine)
      void refreshQueueCount()
    })

    const handleOnline = () => {
      setIsOnline(true)
      setShowReconnected(true)
      // Hide reconnected message after 3 seconds
      setTimeout(() => setShowReconnected(false), 3000)
    }

    const handleOffline = () => {
      setIsOnline(false)
      setShowReconnected(false)
      // Refresh queue count when going offline
      refreshQueueCount()
    }

    // Handle sync status events
    const handleSyncStatus = (e: CustomEvent<{ status: SyncStatus }>) => {
      setSyncStatus(e.detail.status)
      // Refresh queue count on any sync status change
      refreshQueueCount()
      // Auto-clear error status after 5 seconds
      if (e.detail.status === "error") {
        setTimeout(() => setSyncStatus("idle"), 5000)
      }
    }

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)
    window.addEventListener("sync-status" as string, handleSyncStatus as EventListener)

    // Poll queue count every 5 seconds while offline
    const queuePoll = setInterval(() => {
      if (!navigator.onLine) {
        refreshQueueCount()
      }
    }, 5000)

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
      window.removeEventListener("sync-status" as string, handleSyncStatus as EventListener)
      clearInterval(queuePoll)
    }
  }, [refreshQueueCount])

  // Dispatch a manual sync trigger via custom event
  const handleSyncNow = () => {
    window.dispatchEvent(new CustomEvent("trigger-manual-sync"))
  }

  // Show nothing when online, not recently reconnected, and not syncing/error
  if (isOnline && !showReconnected && syncStatus === "idle") {
    return null
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] animate-in slide-in-from-top duration-300">
      {/* Offline indicator - highest priority */}
      {!isOnline ? (
        <div className="bg-red-600 text-white px-4 py-3 flex items-center justify-center gap-2 shadow-lg">
          <WifiOff className="h-5 w-5" />
          <span className="text-sm font-medium">
            You&apos;re offline.
            {queueCount > 0
              ? ` ${queueCount} order${queueCount > 1 ? "s" : ""} pending sync.`
              : " Changes will sync when connection is restored."}
          </span>
          {queueCount > 0 && (
            <span className="text-xs bg-white/20 rounded-full px-2 py-0.5 ml-1">
              {queueCount} queued
            </span>
          )}
        </div>
      ) : syncStatus === "error" ? (
        <div className="bg-orange-500 text-white px-4 py-3 flex items-center justify-center gap-2 shadow-lg">
          <AlertTriangle className="h-5 w-5" />
          <span className="text-sm font-medium">
            Sync failed. Retrying automatically...
          </span>
          <button
            onClick={handleSyncNow}
            className="text-xs bg-white/20 hover:bg-white/30 rounded px-3 py-1 ml-2 font-medium transition-colors min-h-[32px]"
          >
            Sync Now
          </button>
        </div>
      ) : syncStatus === "syncing" ? (
        <div className="bg-blue-500 text-white px-4 py-2 flex items-center justify-center gap-2 shadow-lg">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm font-medium">
            Syncing...
          </span>
        </div>
      ) : showReconnected ? (
        <div className="bg-green-500 text-white px-4 py-2 flex items-center justify-center gap-2 shadow-lg">
          <Wifi className="h-4 w-4" />
          <span className="text-sm font-medium">
            Back online!
          </span>
        </div>
      ) : null}
    </div>
  )
}

// Helper function to dispatch sync status events from anywhere in the app
export function dispatchSyncStatus(status: SyncStatus) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("sync-status", { detail: { status } }))
  }
}
